import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuthStore } from '@/common/stores/useAuthStore';

export default function Login() {
  const { t } = useTranslation();
  const { loginWithGoogle, loginWithEmail, signUpWithEmail } = useAuthStore();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const { error } = isSignUp
      ? await signUpWithEmail(email, password, name)
      : await loginWithEmail(email, password);

    if (error) {
      setError(error.message);
    }
  };

  return (
    <div className="flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-xl shadow-lg">
        <div className="text-center">
          <h2 className="text-2xl font-bold">{isSignUp ? t('login.createAccount') : t('login.signIn')}</h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('login.pleaseContinue', { action: isSignUp ? t('login.signUp') : t('login.signInAction') })}
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="name">
                {t('login.name')}
              </label>
              <input
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                id="name"
                placeholder={t('login.namePlaceholder')}
                required={isSignUp}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="email">
              {t('login.email')}
            </label>
            <input
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              id="email"
              placeholder={t('login.emailPlaceholder')}
              type="email"
              value={email}
              required
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700" htmlFor="password">
              {t('login.password')}
            </label>
            <input
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              id="password"
              placeholder="••••••••"
              type="password"
              value={password}
              required
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            type="submit"
          >
            {isSignUp ? t('login.submitSignUp') : t('login.submitSignIn')}
          </button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">{t('login.orContinueWith')}</span>
          </div>
        </div>

        <button
          className="w-full flex items-center justify-center gap-3 px-4 py-2 border border-gray-300 rounded-md shadow-sm
                   text-sm font-medium text-gray-700 bg-white hover:bg-gray-50
                   transition-all duration-200 ease-in-out transform hover:scale-[1.02]"
          type="button"
          onClick={loginWithGoogle}
        >
          <img
            alt="Google logo"
            className="w-5 h-5"
            src="https://www.google.com/favicon.ico"
          />
          {t('login.continueWithGoogle')}
        </button>

        <div className="text-center">
          <button
            className="text-sm text-blue-600 hover:text-blue-800"
            type="button"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError(null);
              setName('');
            }}
          >
            {isSignUp ? t('login.alreadyHaveAccount') : t('login.noAccount')}
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center">
          {t('login.termsPart1')}
          <Link className="text-blue-600 hover:text-blue-800 hover:underline" target="_blank" to="/terms">
            {t('login.termsOfService')}
          </Link>
          {t('login.termsPart2')}
          <Link className="text-blue-600 hover:text-blue-800 hover:underline" target="_blank" to="/privacy">
            {t('home.privacyPolicy')}
          </Link>
        </p>
      </div>
    </div>
  );
}
